import { LitElement, html, css } from "lit";

/** @type {Record<string, string>} */
const TAB_LABELS = {
  table: "Table",
  card: "Card Catalog",
};

/**
 * Tabbed projection browser for Library media type pages.
 *
 * Owns shared search state (persists across tab switches) and active tab
 * index (defaults to 0, i.e. the first projection in the config). Renders
 * one child projection at a time based on the active tab. Tab order matches
 * the order of the `projections` array.
 *
 * Projection config object shape (built in ERB, mirrors library_config.yml):
 *   { type, displayFields, schema }
 *   type: "table" | "card"
 *
 * @element library-browser
 * @prop {Array}  entries      - Full array of entry objects (each must include a `url` string).
 * @prop {Array}  projections  - Ordered array of projection config objects.
 * @prop {Array}  searchFields - Entry fields to match against the search query.
 * @prop {String} imageField   - Entry field holding the image URL (used by card projection).
 */
export class LibraryBrowserElement extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .library-browser__search {
      margin-bottom: var(--space-lg);
    }

    .library-browser__search input {
      width: 100%;
      box-sizing: border-box;
      font: inherit;
      padding: var(--space-sm) var(--space-md);
      background: var(--color-surface);
      color: var(--color-text);
      border: 1px solid var(--color-border);
      outline: none;
    }

    .library-browser__search input:focus {
      border-color: var(--color-teal);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-teal) 20%, transparent);
    }

    /* Tab strip — active tab sits on top of the dividing border */
    .library-browser__tabs {
      display: flex;
      gap: var(--space-xs);
      margin-bottom: var(--space-lg);
      border-bottom: 2px solid var(--color-teal);
      padding-bottom: 0;
    }

    .library-browser__tabs button {
      background: var(--color-teal);
      border: 1px solid var(--color-teal);
      border-bottom: none;
      cursor: pointer;
      padding: var(--space-xs) var(--space-md);
      font: inherit;
      font-size: var(--step--1);
      font-weight: 700;
      letter-spacing: 0.05em;
      color: var(--color-surface);
      position: relative;
      bottom: -2px;
      transition: color 0.15s ease, border-color 0.15s ease, filter 0.15s ease;
    }

    .library-browser__tabs button:hover {
      filter: brightness(1.1);
    }

    .library-browser__tabs button[aria-selected="true"] {
      background: var(--color-surface);
      border-color: var(--color-border);
      color: var(--color-teal);
    }
  `;

  static properties = {
    entries: { type: Array },
    projections: { type: Array },
    searchFields: { type: Array, attribute: "search-fields" },
    imageField: { type: String, attribute: "image-field" },
    _query: { state: true },
    _activeTab: { state: true },
  };

  constructor() {
    super();
    this.entries = [];
    this.projections = [];
    this.searchFields = [];
    this.imageField = "";
    this._query = "";
    this._activeTab = 0;
    this._boundPopState = this._onPopState.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("popstate", this._boundPopState);
    this._syncTabFromURL();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("popstate", this._boundPopState);
  }

  updated(changedProperties) {
    if (changedProperties.has("projections") && this.projections.length > 0) {
      this._syncTabFromURL();
    }
  }

  _syncTabFromURL() {
    const view = new URLSearchParams(window.location.search).get("view");
    if (!view) return;
    const index = this.projections.findIndex((p) => p.type === view);
    if (index !== -1) this._activeTab = index;
  }

  _onPopState() {
    this._syncTabFromURL();
  }

  /**
   * Switch to the tab at `index` and push a `?view=` entry to browser history
   * so tabs are independently navigable and linkable.
   * @param {number} index
   */
  _setTab(index) {
    this._activeTab = index;
    const type = this.projections[index]?.type;
    const params = new URLSearchParams(window.location.search);
    if (type) {
      params.set("view", type);
    } else {
      params.delete("view");
    }
    history.pushState(
      { view: type },
      "",
      `${window.location.pathname}?${params}${window.location.hash}`,
    );
  }

  get _filtered() {
    const q = this._query.trim().toLowerCase();
    if (!q || !this.searchFields.length) return this.entries;
    return this.entries.filter((entry) =>
      this.searchFields.some((field) => {
        const val = entry[field];
        if (Array.isArray(val))
          return val.some((v) => String(v).toLowerCase().includes(q));
        return String(val ?? "")
          .toLowerCase()
          .includes(q);
      }),
    );
  }

  _renderProjection(config, data) {
    if (!config) return null;

    switch (config.type) {
      case "table":
        return html`<data-table
          .entries="${data}"
          .schema="${config.schema ?? []}"
          .searchFields="${[]}"
        ></data-table>`;

      case "card":
        return html`<data-card-catalog
          .entries="${data}"
          .imageField="${this.imageField}"
          .cardFields="${this.searchFields}"
        ></data-card-catalog>`;

      default:
        return null;
    }
  }

  render() {
    const data = this._filtered;
    const activeConfig = this.projections[this._activeTab] ?? null;

    return html`
      ${this.searchFields.length > 0
        ? html`
            <div class="library-browser__search">
              <input
                type="search"
                placeholder="Search…"
                .value="${this._query}"
                @input="${(e) => (this._query = e.target.value)}"
                aria-label="Search"
              />
            </div>
          `
        : null}
      ${this.projections.length > 1
        ? html`
            <div class="library-browser__tabs" role="tablist">
              ${this.projections.map(
          (proj, i) => html`
                  <button
                    role="tab"
                    aria-selected="${i === this._activeTab}"
                    tabindex="${i === this._activeTab ? 0 : -1}"
                    @click="${() => this._setTab(i)}"
                  >
                    ${TAB_LABELS[proj.type] ?? proj.type}
                  </button>
                `,
        )}
            </div>
          `
        : null}

      <div class="library-browser__panel" role="tabpanel">
        ${this._renderProjection(activeConfig, data)}
      </div>
    `;
  }
}

customElements.define("library-browser", LibraryBrowserElement);
